import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import UnitSearchForm, { FormValues } from "../UnitSearchForm";
import {
    SearchParams,
    UnitStatusEnum,
    useGetUnitsQuery,
} from "../../../../../../services/management/units/api";
import { useGetAccountsQuery } from "../../../../../../services/management/account/api";
import ListItem from "../ListItem";
import Pagination from "../../../../../../shared/components/redesigned/Pagination";
import useTranslation from "../../../../../../shared/hooks/useTranslation";
import useUrlSyncedForm from "../../../../../../shared/hooks/useUrlSyncedForm";

const PER_PAGE = 25;
const VALID_STATUSES = new Set<string>(Object.values(UnitStatusEnum));

function parseStatus(raw: string | null): UnitStatusEnum | undefined {
    return raw && VALID_STATUSES.has(raw) ? (raw as UnitStatusEnum) : undefined;
}

function readUrlParams(url: URLSearchParams): SearchParams {
    return {
        account: url.get("account") || undefined,
        priority: url.get("priority") === "true",
        status: parseStatus(url.get("status")),
        page: Number(url.get("page")) || 1,
        perPage: PER_PAGE,
    };
}

function filterKey(
    account: string | undefined,
    priority: boolean,
    status: UnitStatusEnum | null | undefined,
) {
    return `${account ?? ""}|${priority}|${status ?? ""}`;
}

function serializeFormToUrl(values: Partial<FormValues>, prev: URLSearchParams): URLSearchParams {
    const next = new URLSearchParams(prev);
    if (values.account?.code) next.set("account", values.account.code);
    else next.delete("account");
    if (values.priority) next.set("priority", "true");
    else next.delete("priority");
    if (values.status) next.set("status", values.status);
    else next.delete("status");
    next.delete("page");
    return next;
}

function useAccountByCode(code: string | undefined) {
    const { data, isSuccess, isError } = useGetAccountsQuery(
        { filter: { codeExact: code } },
        { skip: !code },
    );
    return { account: data?.items[0] ?? null, settled: isSuccess || isError };
}

function useSearch() {
    const [urlParams] = useSearchParams();
    const searchParams = useMemo(() => readUrlParams(urlParams), [urlParams]);

    const { account: hydratedAccount, settled: accountFetchSettled } = useAccountByCode(
        searchParams.account,
    );

    // Initial defaults only — URL is treated as source of truth on mount.
    const formDefaults = useRef<FormValues>({
        account: null,
        priority: searchParams.priority,
        status: searchParams.status ?? null,
    }).current;
    const formContext = useForm<FormValues>({ defaultValues: formDefaults });

    // Gates URL ↔ form sync until the initial account lookup settles.
    const [hydrated, setHydrated] = useState(!searchParams.account);
    useEffect(() => {
        if (hydrated) return;
        if (accountFetchSettled) {
            if (hydratedAccount) formContext.setValue("account", hydratedAccount);
            setHydrated(true);
        }
    }, [hydrated, hydratedAccount, accountFetchSettled, formContext]);

    const { setUrlParams } = useUrlSyncedForm<FormValues>({
        formContext,
        hydrated,
        urlKey: filterKey(searchParams.account, searchParams.priority, searchParams.status),
        formKey: (v) => filterKey(v.account?.code, v.priority ?? false, v.status ?? null),
        applyUrlToForm: () => {
            // Defer until any new account code in the URL has been resolved.
            if (searchParams.account && !accountFetchSettled) return false;
            formContext.setValue("account", searchParams.account ? hydratedAccount : null);
            formContext.setValue("priority", searchParams.priority);
            formContext.setValue("status", searchParams.status ?? null);
            return true;
        },
        applyFormToUrl: serializeFormToUrl,
        extraDeps: [accountFetchSettled, hydratedAccount],
    });

    const setPage = useCallback(
        (page: number) =>
            setUrlParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (page > 1) next.set("page", String(page));
                    else next.delete("page");
                    return next;
                },
                { replace: true },
            ),
        [setUrlParams],
    );

    return { formContext, searchParams, setPage };
}

export default function UnitList() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { "*": splat } = useParams();
    const activeUnitId = splat || undefined;

    const { formContext, searchParams, setPage } = useSearch();
    const { data, isFetching } = useGetUnitsQuery(searchParams);
    const [currentUrlParams] = useSearchParams();

    return (
        <div className="@container flex min-h-0 flex-1 flex-col">
            <div className="border-b border-border px-4 py-3">
                <UnitSearchForm context={formContext} />
            </div>
            <div
                className="hidden grid-cols-[5rem_6rem_minmax(0,1fr)_auto] items-center gap-4 border-b border-border bg-surface-2 px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-ink-muted @lg:grid"
                role="row"
            >
                <span>AID</span>
                <span>UOLU ID</span>
                <span>{t("attributes.account.code")}</span>
                <span className="text-right">Status</span>
            </div>
            <div className="relative flex-1 overflow-auto">
                {isFetching && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-surface/60 backdrop-blur-sm">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent/20 border-t-accent" />
                    </div>
                )}
                {data?.items.map((document) => (
                    <ListItem
                        key={document.id}
                        active={String(document.id) === activeUnitId}
                        document={document}
                        onClick={() =>
                            navigate({
                                pathname: String(document.id),
                                search: currentUrlParams.toString(),
                            })
                        }
                    />
                ))}
                {data?.items?.length === 0 && !isFetching && (
                    <div className="flex h-full items-center justify-center p-8 text-sm text-ink-muted">
                        —
                    </div>
                )}
            </div>
            <div className="flex justify-center border-t border-border px-4 py-2">
                <Pagination
                    totalPages={data?.meta?.last ?? 1}
                    currentPage={searchParams.page}
                    onPageChange={setPage}
                />
            </div>
        </div>
    );
}
